import { type OperationVariables, type MutationTuple } from '@apollo/client'
import { useDeepMemo } from '@retailer-platform/shared-common'
import {
  type BasePendingResponse,
  type BaseSuccessResponse,
  type BaseErrorResponse,
} from './baseResponse'

export type BaseMutationResponse<TVariables> = {
  execute: (variables?: TVariables) => void
  reset: () => void
}

export type PendingMutationResponse<TVariables> = BaseMutationResponse<TVariables> &
  BasePendingResponse
export type SuccessMutationResponse<TData, TVariables> = BaseMutationResponse<TVariables> &
  BaseSuccessResponse<TData>
export type ErrorMutationResponse<TVariables> = BaseMutationResponse<TVariables> & BaseErrorResponse

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type MutationResponse<TData = unknown, TVariables = any> =
  | PendingMutationResponse<TVariables>
  | SuccessMutationResponse<TData, TVariables>
  | ErrorMutationResponse<TVariables>

export function useApolloMutationWrapper<
  TResult,
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables
>({
  mutationTuple,
  mapResponse = (data: TData) => data as unknown as TResult,
}: {
  mutationTuple: MutationTuple<TData, TVariables>
  mapResponse: (data: TData) => TResult
}): MutationResponse<TResult, TVariables> {
  return useDeepMemo(() => {
    const [mutate, { loading: isLoading, error, data, reset }] = mutationTuple
    const baseResponse: BaseMutationResponse<TVariables> = {
      execute: (variables?: TVariables) => mutate({ variables }),
      reset,
    }

    if (error) {
      return { type: 'error', isLoading: false, error, ...baseResponse }
    }
    if (!isLoading && !data) {
      return { type: 'pending', isLoading: false, ...baseResponse }
    }
    if (isLoading) {
      return { type: 'pending', isLoading: true, ...baseResponse }
    }

    const parsedData = mapResponse(data!)
    return { type: 'success', isLoading: false, data: parsedData, ...baseResponse }
  }, [mutationTuple, mapResponse])
}
